import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, ArrowLeft, Target, Users, Crown, MessageSquare, HelpCircle, TrendingUp, Award, Heart } from 'lucide-react';
import { StepCard, InfoCard } from './SectionComponents';

const SalesFinalInterviewSection = ({ onBack, onComplete, isCompleted, onNext, nextSectionTitle, hasNext }) => {

  const finalRoundFocus = [
    {
      icon: '🤝',
      title: 'Team fit',
      description: 'Will the team like working with you?'
    },
    {
      icon: '🧠',
      title: 'Coachability',
      description: 'Are you reflective and eager to learn?'
    },
    {
      icon: '💪',
      title: 'Mindset',
      description: 'How do you respond to setbacks, stress and rejection?'
    }
  ];

  const interviewTypes = [
    {
      Icon: Target,
      title: 'Line manager interview',
      description: 'Often with the Head of Sales or a senior AE. Focuses on targets, sales approach, mindset and feedback.',
      commonQuestions: [
        '"How do you handle missing your target?"',
        '"What motivates you when the pipeline doesn\'t look healthy?"',
        '"What kind of feedback has helped you improve most?"'
      ]
    },
    {
      Icon: Crown,
      title: 'Founder interview',
      description: 'Especially common in Seed to Series A startups. Focuses on mission alignment, commercial awareness and grit.',
      commonQuestions: [
        '"What would you focus on in your first month here?"',
        '"What makes you different from other SDRs or AEs?"',
        '"When have you had to figure something out completely on your own?"'
      ]
    },
    {
      Icon: Heart,
      title: 'Team fit interview / culture chat',
      description: 'Could be with peers, cross-functional teammates or a founder. Focuses on how you work with others, energy and communication.',
      commonQuestions: [
        'Often more casual in tone, but just as important',
        'You should scour the startup\'s video pitch, website & JD for any references to values or culture',
        'Think of 2-3 examples where you\'ve shown these traits'
      ]
    }
  ];

  const greatQuestions = [
    '"What separates your best-performing reps from the rest?"',
    '"What does ramp-up success look like in month 1 vs. month 3?"',
    '"What\'s a challenge the sales team is currently facing that\'s under-discussed?"',
    '"How do you see this role evolving over the next 6-12 months?"'
  ];

  const questionsToAvoid = [
    {
      avoid: '"What\'s your culture like?"',
      better: '"How would the team describe your culture when things aren\'t going to plan?"'
    },
    {
      avoid: '"What\'s the commission structure?"',
      better: 'Save this for after an offer'
    }
  ];

  const checklistItems = [
    'Prepared at least 3 thoughtful, well-researched questions',
    'Considered how to demonstrate a growth mindset — e.g. what you could have done better in the previous rounds',
    'Look at the startup\'s video pitch, website & JD for any references to values or culture and think of 2-3 examples where you\'ve shown these traits',
    'Know your 2-3 top impact stories, with clear, evidence-backed examples of your best work',
    'Refresh your response to "why this startup?" — showing you understand the product and the market',
    'Be prepared to bring good energy / vibes to show you\'re an excellent team fit'
  ];

  return (
    <div className="bg-white min-h-screen">
      {/* Hero Section */}
      <div className="w-full py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12"
        >
          <div className="text-center mb-16">
            <h1 className="text-6xl font-bold text-gray-900 mb-6">
              Final interviews: sealing the deal
            </h1>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Navigate the final stage with confidence and close your dream sales role.
            </p>
          </div>
          <div className="flex items-center justify-center space-x-6">
            <button
              onClick={onBack}
              className="inline-flex items-center px-8 py-4 bg-white text-gray-700 rounded-2xl font-semibold hover:bg-gray-50 transition-all duration-300 shadow-lg border border-gray-200"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Resources
            </button>
            <button
              onClick={onComplete}
              className={`inline-flex items-center px-8 py-4 rounded-2xl font-semibold transition-all duration-300 shadow-lg ${
                isCompleted
                  ? 'bg-[#02B491] text-white shadow-emerald-500/30'
                  : 'bg-[#02B491] text-white hover:bg-emerald-600 shadow-emerald-500/20'
              }`}
            >
              {isCompleted ? (
                <>
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Completed
                </>
              ) : (
                'Mark as Complete'
              )}
            </button>
          </div>
        </motion.div>
      </div>

      {/* Content */}
      <div className="w-full pb-20 bg-white">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 space-y-16">
          
          <StepCard title="What final rounds are really about">
            <div className="space-y-8">
              <div className="bg-green-50 border border-green-200 rounded-2xl p-6">
                <h5 className="font-bold text-gray-900 mb-4 text-lg">By this stage, you've likely shown:</h5>
                <div className="space-y-2">
                  {[
                    'You understand the product and the sales process',
                    'You\'ve got the energy to thrive in a startup sales role'
                  ].map((item, i) => (
                    <div key={i} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-800 leading-relaxed">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div>
                <h4 className="font-bold text-slate-900 mb-6 text-xl">Now the focus shifts to:</h4>
                <div className="grid gap-6 md:grid-cols-3">
                  {finalRoundFocus.map((focus, index) => (
                    <div key={index} className="bg-white rounded-2xl p-6 border border-gray-200 hover:shadow-lg hover:shadow-gray-900/5 transition-all duration-300">
                      <div className="flex items-center gap-3 mb-4">
                        <span className="text-2xl">{focus.icon}</span>
                        <h5 className="font-bold text-gray-900 text-lg">{focus.title}</h5>
                      </div>
                      <p className="text-gray-600 leading-relaxed">{focus.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </StepCard>

          <StepCard title="Types of final round interviews you might face">
            <div className="space-y-10">
              {interviewTypes.map((type, index) => (
                <div key={index} className="space-y-6">
                  <div className="space-y-4">
                    <h4 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                      <type.Icon className="w-8 h-8 text-[#02B491]" />
                      {type.title}
                    </h4>
                    <p className="text-gray-600 text-lg leading-relaxed ml-11">
                      {type.description}
                    </p>
                  </div>
                  {type.commonQuestions && (
                    <div className="bg-white rounded-2xl p-6 border border-gray-200 ml-11">
                      {index < 2 && <h5 className="font-semibold text-gray-900 mb-4 text-lg">Common questions:</h5>}
                      <div className="space-y-3">
                        {type.commonQuestions.map((question, i) => (
                          <div key={i} className="flex items-start space-x-3">
                            <CheckCircle className="w-5 h-5 text-[#02B491] mt-1 flex-shrink-0" />
                            <span className="text-gray-700 leading-relaxed">
                              {index === 2 ? question : `${question}`}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </StepCard>

          <StepCard title="Asking great questions in a final round interview">
            <div className="space-y-10">
              <div>
                <div className="flex items-center gap-2 mb-6">
                  <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                  <h4 className="font-semibold text-gray-900 text-lg">Try questions like:</h4>
                </div>
                <div className="space-y-4">
                  {greatQuestions.map((question, i) => (
                    <div key={i} className="bg-white rounded-xl p-4 border-l-4 border-green-400">
                      <p className="text-gray-700 leading-relaxed font-semibold text-base">
                        {question}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-t border-gray-100 pt-8">
                <div className="flex items-center gap-2 mb-6">
                  <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                  <h4 className="font-semibold text-gray-900 text-lg">Avoid:</h4>
                </div>
                <div className="space-y-6">
                  {questionsToAvoid.map((item, i) => (
                    <div key={i} className="bg-red-50 border border-red-200 rounded-xl p-6">
                      <div className="space-y-3">
                        <div>
                          <span className="font-semibold text-red-900">Don't ask:</span>
                          <span className="text-red-800 ml-2">{item.avoid}</span>
                        </div>
                        <div>
                          <span className="font-semibold text-green-900">Instead:</span>
                          <span className="text-green-800 ml-2">{item.better}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </StepCard>

          <StepCard title="📋 Final interview checklist">
            <div className="space-y-6">
              <p className="text-gray-600 text-lg leading-relaxed">
                Before your final interview, make sure you've:
              </p>
              
              <div className="grid gap-3">
                {checklistItems.map((item, i) => (
                  <div key={i} className="flex items-start space-x-3 bg-white rounded-xl p-4 border border-gray-200">
                    <CheckCircle className="w-5 h-5 text-[#02B491] mt-1 flex-shrink-0" />
                    <span className="text-gray-800 leading-relaxed">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </StepCard>
        </div>

        {hasNext && (
          <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 pt-12">
            <div className="text-center">
              <button
                onClick={onNext}
                className="inline-flex items-center px-8 py-4 bg-white text-[#02B491] rounded-2xl font-semibold hover:bg-gray-50 transition-all duration-300 shadow-lg border-2 border-[#02B491] hover:shadow-emerald-500/20"
              >
                Continue to: {nextSectionTitle}
                <svg className="w-5 h-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SalesFinalInterviewSection; 